import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import { Input } from "@/components/base/Field";
import { useToast } from "@/components/base/Toast";
import { uid } from "@/services/api";
import { updateArticles, updateFicheTexts, type LoadedFiche } from "@/services/ficheService";
import { formatMoney } from "@/utils/format";
import type { FicheArticle, PaymentPeriod } from "@/types/fiche";
import type { Cycle, SchoolClass } from "@/types";
import { currencySymbol } from "@/utils/currency";

interface Props {
  open: boolean;
  onClose: () => void;
  fiche: LoadedFiche;
  classes: SchoolClass[];
  cycles: Cycle[];
  onChanged: () => void;
}

export default function ArticlesPeriodsModal({ open, onClose, fiche, classes, cycles, onChanged }: Props) {
  const { articles, periods } = fiche;
  const { notify } = useToast();
  const [items, setItems] = useState<FicheArticle[]>([]);
  const [rows, setRows] = useState<PaymentPeriod[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setItems(articles.map((a) => ({ ...a })));
    setRows(periods.map((p) => ({ ...p })));
  }, [open, articles, periods]);

  const updateItem = (id: string, patch: Partial<FicheArticle>) =>
    setItems((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));

  const addItem = () =>
    setItems((prev) => [...prev, { id: uid("art"), label: "", amount: 0, appliesToAllClasses: true, classIds: [] }]);

  const toggleClass = (a: FicheArticle, classId: string) =>
    updateItem(a.id, {
      appliesToAllClasses: false,
      classIds: a.classIds.includes(classId) ? a.classIds.filter((c) => c !== classId) : [...a.classIds, classId],
    });

  const setCycleClasses = (a: FicheArticle, cycleId: string) =>
    updateItem(a.id, {
      appliesToAllClasses: false,
      classIds: classes.filter((c) => c.cycleId === cycleId).map((c) => c.id),
    });

  const removeItem = (id: string) => setItems((prev) => prev.filter((a) => a.id !== id));

  const updateRow = (id: string, patch: Partial<PaymentPeriod>) =>
    setRows((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  const addRow = () =>
    setRows((prev) => [...prev, { id: uid("per"), label: `Tranche ${prev.length + 1}`, value: "" }]);

  const removeRow = (id: string) => setRows((prev) => prev.filter((p) => p.id !== id));

  const handleSave = async () => {
    setBusy(true);
    try {
      const cleanArticles = items
        .filter((a) => a.label.trim())
        .map((a) => ({ ...a, label: a.label.trim(), amount: Number(a.amount) || 0 }));
      const invalid = cleanArticles.find((a) => a.amount <= 0 || (!a.appliesToAllClasses && a.classIds.length === 0));
      if (invalid) {
        notify(`« ${invalid.label} » : indiquez un montant et au moins une classe (ou « Toutes les classes »).`, "error");
        return;
      }
      const cleanPeriods = rows.filter((p) => p.label.trim() || p.value.trim());
      await updateArticles(fiche, cleanArticles);
      await updateFicheTexts(fiche, { periods: cleanPeriods });
      notify("Articles et périodes de recouvrement mis à jour.");
      onChanged();
      onClose();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Erreur lors de l'enregistrement.", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Articles & périodes de recouvrement"
      subtitle="Tenues, fournitures vendues à l'école et rappel des échéances"
      icon="ri-shopping-bag-3-line"
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button icon="ri-save-3-line" disabled={busy} onClick={handleSave}>
            {busy ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </>
      }
    >
      <div className="space-y-7">
        <section>
          <div className="mb-2 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500">
                Articles vendus à l'école
              </p>
              <p className="mt-0.5 text-xs text-foreground-400">
                Un article est dû par chaque élève des classes cochées (ou de toutes les classes).
              </p>
            </div>
            <Button size="sm" variant="outline" icon="ri-add-line" onClick={addItem}>
              Ajouter
            </Button>
          </div>

          <div className="space-y-2">
            {items.map((a) => (
              <div
                key={a.id}
                className="rounded-md border border-background-200 p-3"
              >
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_150px_auto] sm:items-end">
                <Input
                  label="Désignation"
                  placeholder="ex. Tenue — Primaire"
                  value={a.label}
                  onChange={(e) => updateItem(a.id, { label: e.target.value })}
                />
                <Input
                  label={`Montant (${currencySymbol()})`}
                  type="number"
                  min={0}
                  value={String(a.amount)}
                  onChange={(e) => updateItem(a.id, { amount: Number(e.target.value) })}
                />
                <button
                  type="button"
                  onClick={() => removeItem(a.id)}
                  disabled={a.hasPayments}
                  aria-label="Supprimer l'article"
                  title={a.hasPayments ? "Des versements existent : l'article ne peut pas être retiré" : undefined}
                  className="h-9 w-9 shrink-0 flex items-center justify-center rounded-md text-foreground-400 transition-colors hover:bg-accent-50 hover:text-accent-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
                </div>
                <div className="mt-2.5">
                  <div className="mb-1.5 flex flex-wrap items-center gap-1.5 text-xs">
                    <span className="text-foreground-500">Dû par :</span>
                    <button
                      type="button"
                      onClick={() => updateItem(a.id, { appliesToAllClasses: true, classIds: [] })}
                      className={`rounded-full border px-2.5 py-0.5 cursor-pointer ${a.appliesToAllClasses ? "border-primary-500 bg-primary-600 text-white" : "border-background-300 text-foreground-700 hover:border-primary-300"}`}
                    >
                      Toutes les classes
                    </button>
                    {cycles.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setCycleClasses(a, c.id)}
                        className="rounded-full border border-background-300 px-2.5 py-0.5 text-foreground-700 hover:border-primary-300 cursor-pointer"
                      >
                        Tout le {c.label.toLowerCase()}
                      </button>
                    ))}
                  </div>
                  {!a.appliesToAllClasses && (
                    <div className="flex flex-wrap gap-1.5">
                      {classes.map((c) => {
                        const on = a.classIds.includes(c.id);
                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => toggleClass(a, c.id)}
                            className={`rounded-md border px-2 py-0.5 text-xs cursor-pointer ${on ? "border-primary-400 bg-primary-50 text-primary-800" : "border-background-200 bg-background-50 text-foreground-600 hover:border-background-300"}`}
                          >
                            {on && <i className="ri-check-line mr-0.5"></i>}
                            {c.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {items.length === 0 && (
              <p className="rounded-md border border-dashed border-background-300 py-5 text-center text-xs text-foreground-400">
                Aucun article. Ajoutez les tenues et fournitures vendues à l'école.
              </p>
            )}
          </div>

          {items.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {items.map((a) => (
                <Badge key={a.id} tone="secondary">
                  {a.label || "Sans nom"} · {formatMoney(a.amount)}
                </Badge>
              ))}
            </div>
          )}
        </section>

        <section>
          <div className="mb-2 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500">
                Périodes de recouvrement
              </p>
              <p className="mt-0.5 text-xs text-foreground-400">
                Rappel affiché en bas de la fiche imprimée.
              </p>
            </div>
            <Button size="sm" variant="outline" icon="ri-add-line" onClick={addRow}>
              Ajouter
            </Button>
          </div>

          <div className="space-y-2">
            {rows.map((p) => (
              <div
                key={p.id}
                className="grid grid-cols-1 gap-2 rounded-md border border-background-200 p-3 sm:grid-cols-[200px_1fr_auto] sm:items-end"
              >
                <Input
                  label="Intitulé"
                  placeholder="ex. Deuxième tranche"
                  value={p.label}
                  onChange={(e) => updateRow(p.id, { label: e.target.value })}
                />
                <Input
                  label="Période"
                  placeholder="ex. Du 05 Décembre 2026 au 05 Janvier 2027"
                  value={p.value}
                  onChange={(e) => updateRow(p.id, { value: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => removeRow(p.id)}
                  aria-label="Supprimer la période"
                  className="h-9 w-9 shrink-0 flex items-center justify-center rounded-md text-foreground-400 transition-colors hover:bg-accent-50 hover:text-accent-600 cursor-pointer"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            ))}
            {rows.length === 0 && (
              <p className="rounded-md border border-dashed border-background-300 py-5 text-center text-xs text-foreground-400">
                Aucune période définie.
              </p>
            )}
          </div>
        </section>
      </div>
    </Modal>
  );
}